import { Box, Dialog, DialogContent, DialogProps, DialogTitle } from '@mui/material'
import { CatalogProductType } from '../enums'
import { useTranslation } from 'react-i18next'
import CatalogProductForm from './CatalogProductForm'
import { CatalogProduct } from '../types'

export interface CatalogProductCreateDialogProps extends Omit<DialogProps, 'children' | 'onClose'> {
  type?: CatalogProductType
  afterCreate?: (catalogProduct: CatalogProduct) => void
  onClose?: () => void
}

/**
 * The translation keys of the catalog product type names.
 */
const typeTranslationKeys: Record<CatalogProductType, string> = {
  [CatalogProductType.ELECTRICAL_CABINET]: 'catalogProduct:electricalCabinet',
  [CatalogProductType.SPINE_MODULE]: 'catalogProduct:spineModule',
  [CatalogProductType.SPINE_MULTI_MEASUREMENT]: 'catalogProduct:spineMultiMeasurement',
  [CatalogProductType.RUUVI_TAG]: 'catalogProduct:ruuviTag',
  [CatalogProductType.HEATER]: 'catalogProduct:heater',
  [CatalogProductType.GENERATOR]: 'catalogProduct:generator',
  [CatalogProductType.AURORA]: 'catalogProduct:aurora',
  [CatalogProductType.AXIOMA_W1]: 'catalogProduct:axiomaW1',
  [CatalogProductType.SPINE_LOAD]: 'catalogProduct:spineLoad',
  [CatalogProductType.VALVE]: 'catalogProduct:valve'
}

export default function CatalogProductCreateDialog({
  type,
  afterCreate,
  onClose,
  open,
  ...props
}: CatalogProductCreateDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * Handle the "after submit" event.
   */
  function handleAfterSubmit(catalogProduct: CatalogProduct) {
    afterCreate?.(catalogProduct)
    onClose?.()
  }

  /**
   * Handle the "cancel" event.
   */
  function handleCancel() {
    onClose?.()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body" {...props}>
      <DialogTitle>
        {type !== undefined
          ? t('newCatalogProductOfType', { type: t(typeTranslationKeys[type]) })
          : t('newCatalogProduct')}
      </DialogTitle>
      {/* The dialog scrolls as a whole, so the content must not add scrollbars of its own. */}
      <DialogContent sx={{ overflowY: 'visible' }}>
        <Box sx={{ mt: 2 }}>
          <CatalogProductForm type={type} afterSubmit={handleAfterSubmit} onCancel={handleCancel} />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
